import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {DeviceMarker} from '../../../floor-plan-shared/models/device-marker';
import {DraggableDeviceMarkerComponent} from '../draggable-device-marker/draggable-device-marker.component';
import {DropItem} from '../../../drag-and-drop/models/drag-and-drop';
import {CanvasMapInitializedVariables} from '../../../floor-plan-shared/models/canvas-map';

@Component({
  selector: 'awair-floor-plan-editor-map',
  templateUrl: './floor-plan-editor-map.component.html',
  styleUrls: ['./floor-plan-editor-map.component.scss']
})

export class FloorPlanEditorMapComponent implements OnInit {

  @ViewChild('markerAnchor', {read: ViewContainerRef}) markerAnchor;

  @Output() dropDevice: EventEmitter<{relativeXPercentage: number, relativeYPercentage: number, data?: any}> =
    new EventEmitter<{relativeXPercentage: number, relativeYPercentage: number, data?: any}>();
  @Output() removeMarker: EventEmitter<string> = new EventEmitter<string>();

  canvasWidth: number = 0;
  canvasHeight: number = 0;
  imageWidth: number = 0;
  imageHeight: number = 0;
  canvasDestinationX: number = 0;
  canvasDestinationY: number = 0;
  zoomRatio: number;

  private deviceMarkerEntities: {[deviceUuid: string]: DeviceMarker} = {};
  private deviceMarkerComponentRefMap: { [deviceUuid: string]: ComponentRef<DraggableDeviceMarkerComponent> } = {};

  constructor(private sanitizer: DomSanitizer,
              private componentFactoryResolver: ComponentFactoryResolver) {
  }

  @Input()
  set addedDeviceMarkers(value: DeviceMarker[]) {
    if (value != null) {
      value.forEach((addedDeviceMarker: DeviceMarker) => {
        this.deviceMarkerComponentRefMap[addedDeviceMarker.deviceUuid] = this.addDeviceMarker(addedDeviceMarker);
        this.deviceMarkerEntities[addedDeviceMarker.deviceUuid] = {...addedDeviceMarker};
      });
    }
  }

  @Input()
  set updatedDeviceMarker(value: DeviceMarker) {
    if (value != null && this.deviceMarkerComponentRefMap[value.deviceUuid]) {
      this.deviceMarkerComponentRefMap[value.deviceUuid].instance.hostLeft = this.sanitizer.bypassSecurityTrustStyle(
        this.getMarkerPoint(this.imageWidth, value.x, this.zoomRatio, this.canvasDestinationX));
      this.deviceMarkerComponentRefMap[value.deviceUuid].instance.hostTop = this.sanitizer.bypassSecurityTrustStyle(
        this.getMarkerPoint(this.imageHeight, value.y, this.zoomRatio, this.canvasDestinationY));

      // this.deviceMarkerComponentRefMap[updatedDeviceMarker.deviceUuid].instance.color
      this.deviceMarkerEntities[value.deviceUuid] = {...value};
    }
  }

  @Input()
  set removedDeviceMarkerUuids(value: string[]) {
    if (value != null) {
      value.forEach((removedDeviceMarkerUuid: string) => {
        if (this.deviceMarkerComponentRefMap[removedDeviceMarkerUuid]) {
          this.deviceMarkerComponentRefMap[removedDeviceMarkerUuid].destroy();
          this.deviceMarkerComponentRefMap[removedDeviceMarkerUuid] = null;

          delete this.deviceMarkerEntities[removedDeviceMarkerUuid];
        }
      });
    }
  }

  get mapUrl(): string {
    if (this._mapUrl != null && this._mapUrl.length > 0) {
      return this._mapUrl;
    }
  }
  @Input()
  set mapUrl(value: string) {
    if (value != null && value.length > 0) {
      this._mapUrl = value;
    }
  }
  private _mapUrl: string;

  ngOnInit() {
  }

  // Initialize device markers after canvas has been initialized
  initDeviceMarkers({imageWidth, imageHeight, dx, dy, canvasWidth, canvasHeight, zoomRatio}: CanvasMapInitializedVariables) {
    this.canvasDestinationX = dx;
    this.canvasDestinationY = dy;
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.imageWidth = imageWidth;
    this.imageHeight = imageHeight;
    this.zoomRatio = zoomRatio;

    this.updateDeviceMarkersPosition();
  }

  onChangeImagePosition({dx, dy}: {dx: number, dy: number}): void {
    this.canvasDestinationX = dx;
    this.canvasDestinationY = dy;

    this.updateDeviceMarkersPosition();
  }

  onChangeZoomRatio(zoomRatio: number): void {
    this.zoomRatio = zoomRatio;

    this.updateDeviceMarkersPosition();
  }

  onDeviceDrop(dropItem: DropItem): void {
    const imageRelativeX = (dropItem.relativeX - this.canvasDestinationX) / this.zoomRatio;
    const imageRelativeY = (dropItem.relativeY - this.canvasDestinationY) / this.zoomRatio;

    const imageRelativeXPercentage = imageRelativeX / this.imageWidth;
    const imageRelativeYPercentage = imageRelativeY / this.imageHeight;

    if (imageRelativeXPercentage > 0 && imageRelativeXPercentage < 1 && imageRelativeYPercentage > 0 && imageRelativeYPercentage < 1) {
      this.dropDevice.emit({
        relativeXPercentage: imageRelativeXPercentage,
        relativeYPercentage: imageRelativeYPercentage,
        data: dropItem.data
      });
    }
  }

  private updateDeviceMarkersPosition() {
    Object.keys(this.deviceMarkerComponentRefMap).forEach((deviceUuid: string) => {
      this.deviceMarkerComponentRefMap[deviceUuid].instance.hostLeft = this.sanitizer.bypassSecurityTrustStyle(
        this.getMarkerPoint(this.imageWidth, this.deviceMarkerEntities[deviceUuid].x, this.zoomRatio, this.canvasDestinationX));
      this.deviceMarkerComponentRefMap[deviceUuid].instance.hostTop = this.sanitizer.bypassSecurityTrustStyle(
        this.getMarkerPoint(this.imageHeight, this.deviceMarkerEntities[deviceUuid].y, this.zoomRatio, this.canvasDestinationY));
    });
  }

  private addDeviceMarker({x, y, deviceUuid, name}: { x: number, y: number, deviceUuid: string, name?: string }):
    ComponentRef<DraggableDeviceMarkerComponent> {

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(DraggableDeviceMarkerComponent);

    const componentRef = this.markerAnchor.createComponent(componentFactory);
    (<DraggableDeviceMarkerComponent>componentRef.instance).hostLeft = this.sanitizer.bypassSecurityTrustStyle(
      this.getMarkerPoint(this.imageWidth, x, this.zoomRatio, this.canvasDestinationX));
    (<DraggableDeviceMarkerComponent>componentRef.instance).hostTop = this.sanitizer.bypassSecurityTrustStyle(
      this.getMarkerPoint(this.imageHeight, y, this.zoomRatio, this.canvasDestinationY));
    (<DraggableDeviceMarkerComponent>componentRef.instance).color = '#64C2E6';
    (<DraggableDeviceMarkerComponent>componentRef.instance).deviceUuid = deviceUuid;
    if (name) {
      (<DraggableDeviceMarkerComponent>componentRef.instance).name = name;
    }
    (<DraggableDeviceMarkerComponent>componentRef.instance).remove.subscribe((removedDeviceUuid: string) => {
      this.removeMarker.emit(removedDeviceUuid);
    });

    return componentRef;
  }

  private getMarkerPoint(size: number, ratio: number, zoomRatio: number, destinationPoint: number): string {
    return `calc((${size}px * ${ratio}) * ${zoomRatio} - 20px + ${destinationPoint}px)`;
  }
}
